import type {
  CompletenessLabel,
  NeedItem,
  NextStep,
  RoleDraft,
  SufficiencyLevel,
  TitleVerdict,
  Verdict,
} from './types';
import { isStrongVerdict } from './verdict';

export function toScreeningQuestion(value: string): string {
  const v = value.trim().replace(/[.?!]+$/, '');
  return `Evidence of ${v.charAt(0).toLowerCase()}${v.slice(1)}?`;
}

export function deriveNeeds(
  role: RoleDraft,
  titleVerdict: TitleVerdict | null,
): NeedItem[] {
  const title = role.title.trim();
  const titleItem: NeedItem = !title
    ? {
        id: 'title',
        label: 'Job title',
        status: 'todo',
        hint: 'Add a job title — it drives who we search for.',
      }
    : titleVerdict === 'vague'
      ? {
          id: 'title',
          label: 'Job title',
          status: 'todo',
          hint: 'Too broad or non-standard to search on — add a specialism (a stack, domain, or function).',
        }
      : { id: 'title', label: 'Job title', status: 'done' };

  const locationItem: NeedItem = role.location.trim()
    ? { id: 'location', label: 'Location', status: 'done' }
    : {
        id: 'location',
        label: 'Location',
        status: 'todo',
        hint: 'Add where candidates may work. For remote roles, list eligible countries.',
      };

  const seniorityItem: NeedItem = role.seniority.length
    ? { id: 'seniority', label: 'Seniority', status: 'done' }
    : {
        id: 'seniority',
        label: 'Seniority',
        status: 'todo',
        hint: 'Set a seniority level to sharpen the shortlist.',
      };

  const companiesItem: NeedItem = role.companies.trim()
    ? { id: 'companies', label: 'Example companies', status: 'done' }
    : {
        id: 'companies',
        label: 'Example companies',
        status: 'optional',
        hint: 'Name a few companies with great people in this role.',
      };

  return [titleItem, locationItem, seniorityItem, companiesItem];
}

/* ── copy for the `/ai/job-signal` scores (the backend owns the logic; the UI owns the words) ─────────────── */

const NEXT_STEP_COPY: Record<NextStep, string> = {
  add_title: 'Add a job title',
  sharpen_title: 'Sharpen the job title',
  add_searchable_skills: 'Add searchable must-have skills',
  add_more_skills: 'Add more must-have skills to narrow the search',
  add_city: 'Add a location',
  set_seniority: 'Set a seniority level',
};

/** The sentence for a completeness `nextStep` key. */
export function nextStepCopy(nextStep: NextStep): string {
  return NEXT_STEP_COPY[nextStep];
}

const COMPLETENESS_LABEL_TEXT: Record<CompletenessLabel, string> = {
  strong: 'strong',
  getting_there: 'getting there',
  thin: 'thin',
};

/** Display text for a completeness `label` (the wire enum → the words the prototype used). */
export function completenessLabelText(label: CompletenessLabel): string {
  return COMPLETENESS_LABEL_TEXT[label];
}

/** The skills check's words beside a skill: strong ones are searchable, flagged ones say why. */
export function verdictShortText(verdict: Verdict): string {
  if (isStrongVerdict(verdict)) return 'searchable';
  if (verdict === 'ubiquitous') return 'too common';
  if (verdict === 'vague') return 'not a skill';
  return 'unknown';
}

/**
 * The skill-sufficiency message. The backend owns the `level` bucket; the words — and the skill name in the
 * single-strong-skill case — are composed here from the display labels the panel already has.
 */
export function skillSufficiencyMessage(
  level: SufficiencyLevel,
  strongLabels: string[],
): string {
  if (level === 'ok') return 'Enough searchable skills to narrow the search.';
  // `none`, or a transient where the (lagging) level still says `thin` but the live strong list is already empty —
  // guarding on the live count avoids a nonsense "Only 0 searchable skills" during the refetch window.
  if (level === 'none' || strongLabels.length === 0) {
    return 'No searchable skills yet — add specific tools or technologies.';
  }
  return strongLabels.length === 1
    ? `${strongLabels[0]} alone matches a huge number of people — add 2–3 more must-have skills to narrow the search.`
    : `Only ${strongLabels.length} searchable skills — add a couple more to narrow the search.`;
}
